import React, { ReactNode, useEffect, useState } from 'react';
import { localizeNumber } from '@repo/utils/helpers/number';
import { LoaderIcon } from '@repo/icons';
import { CSVModalButton, CSVModalButtonRow } from '../../Buttons';
import { AlertBox } from '../Review';
import { supportEmailProps } from '@repo/utils/helpers/preferred-email-client';
import styles from '../Upload/Upload.module.scss';

export default function ImportView({
  forceDarkMode,
  startImport,
  erroredRowCount,
  warnedRowCount,
  importableRowCount,
  goBack,
  errorCreatingImport,
}: {
  forceDarkMode: boolean;
  startImport: () => void;
  erroredRowCount: number;
  warnedRowCount: number;
  importableRowCount: number;
  goBack: () => void;
  errorCreatingImport: boolean;
}) {
  const [buttonClicked, setButtonClicked] = useState(false);
  let message: ReactNode = '';

  if (erroredRowCount === 0 && warnedRowCount === 0) {
    message = (
      <p>
        We can import all {localizeNumber(importableRowCount)}{' '}
        {importableRowCount > 1 ? 'rows' : 'row'} from the provided CSV.
      </p>
    );
  } else if (erroredRowCount === 0 && warnedRowCount > 0) {
    message = (
      <p>
        We can import all {localizeNumber(importableRowCount)}{' '}
        {importableRowCount > 1 ? 'rows' : 'row'} from the provided CSV. Are you sure you want to
        ignore the{' '}
        <b>
          {localizeNumber(warnedRowCount)} {warnedRowCount > 1 ? 'warnings' : 'warning'}
        </b>{' '}
        and go ahead with the import?
      </p>
    );
  } else if (erroredRowCount > 0 && warnedRowCount === 0) {
    message = (
      <p>
        We can import {localizeNumber(importableRowCount)} {importableRowCount > 1 ? 'rows' : 'row'}{' '}
        from the provided CSV. Are you sure you want to ignore the{' '}
        <b>
          {localizeNumber(erroredRowCount)} errored {erroredRowCount > 1 ? 'rows' : 'row'}
        </b>{' '}
        and go ahead with the import?
      </p>
    );
  } else if (erroredRowCount > 0 && warnedRowCount > 0) {
    message = (
      <p>
        We can import {localizeNumber(importableRowCount)} {importableRowCount > 1 ? 'rows' : 'row'}{' '}
        from the provided CSV. Are you sure you want to ignore the{' '}
        <b>
          {localizeNumber(erroredRowCount)} errored {erroredRowCount > 1 ? 'rows' : 'row'}
        </b>{' '}
        and{' '}
        <b>
          {localizeNumber(warnedRowCount)} {warnedRowCount > 1 ? 'warnings' : 'warning'}
        </b>{' '}
        and go ahead with the import?
      </p>
    );
  }

  const onStartImportClick = () => {
    setButtonClicked(true);
    startImport();
  };

  useEffect(() => {
    if (errorCreatingImport) {
      setButtonClicked(false);
    }
  }, [errorCreatingImport]);

  return (
    <>
      {message}

      {errorCreatingImport && (
        <AlertBox
          className={styles.errorBox}
          forceDarkMode={forceDarkMode}
          status="error"
          title="Sorry, something went wrong"
        >
          There was an error uploading the CSV. Please try again. If the problem persists, please{' '}
          <a {...supportEmailProps({ subject: 'CSV import error' })}>reach out to us</a>.
        </AlertBox>
      )}

      <CSVModalButtonRow>
        <CSVModalButton
          onClick={goBack}
          secondary
          forceDarkMode={forceDarkMode}
          disabled={buttonClicked}
        >
          Back
        </CSVModalButton>
        <CSVModalButton
          onClick={onStartImportClick}
          forceDarkMode={forceDarkMode}
          disabled={buttonClicked}
        >
          {buttonClicked ? (
            <>
              Starting Import <LoaderIcon width="12" />
            </>
          ) : (
            'Start Import'
          )}
        </CSVModalButton>
      </CSVModalButtonRow>
    </>
  );
}
